import {
  TypographyCaption,
  TypographyP2,
  TypographyP2SemiBold,
} from '@heidi/design-system/typography';
import { FolderOpen, ListChecks } from 'lucide-react';
import { useMemo, type ReactNode } from 'react';
import { useIntl, type IntlShape, type MessageDescriptor } from 'react-intl';

import { objectWorkspaceMessages as messages } from './ObjectWorkspace.messages';
import {
  AnchorList,
  AnchorListLoading,
  PaginatedAnchorList,
} from './ObjectWorkspaceAnchorList';
import { ObjectWorkspacePatientDetails } from './ObjectWorkspacePatientDetails';
import { ObjectWorkspaceWorkContent } from './ObjectWorkspaceWorkContent';
import {
  PATIENT_HISTORY,
  type ObjectWorkspaceAnchor,
  type ObjectWorkspaceAnchorSource,
  type ObjectWorkspaceFacet,
  type ObjectWorkspaceObject,
  type ObjectWorkspacePatientDetailsState,
  type ObjectWorkspacePatientSessionsState,
  type ObjectWorkspaceWorkEngagement,
} from './objectWorkspace.data';

interface AnchorSectionProps {
  heading: MessageDescriptor;
  children: ReactNode;
}

function AnchorSection({ heading, children }: AnchorSectionProps): JSX.Element {
  const intl = useIntl();

  return (
    <section className="flex flex-col gap-3">
      <TypographyP2SemiBold className="text-text-primary">
        {intl.formatMessage(heading)}
      </TypographyP2SemiBold>
      {children}
    </section>
  );
}

function PatientSessionsFacet({
  state,
  onSelect,
}: {
  state: ObjectWorkspacePatientSessionsState;
  onSelect?: (anchor: ObjectWorkspaceAnchor) => void;
}): JSX.Element {
  const intl = useIntl();

  let content: ReactNode;
  if (state.status === 'loading') {
    content = <AnchorListLoading />;
  } else if (state.status === 'error') {
    content = (
      <TypographyP2 className="text-text-secondary">
        {intl.formatMessage(messages.patientSessionsUnavailable)}
      </TypographyP2>
    );
  } else if (state.sessions.length === 0) {
    content = (
      <TypographyP2 className="text-text-secondary">
        {intl.formatMessage(messages.noPatientSessions)}
      </TypographyP2>
    );
  } else {
    content = (
      <PaginatedAnchorList
        anchors={state.sessions}
        onSelect={onSelect}
        hasMore={state.hasMore}
        isLoadingMore={state.isLoadingMore}
        onShowMore={state.onShowMore}
      />
    );
  }

  return <AnchorSection heading={messages.sessionsHeading}>{content}</AnchorSection>;
}

function resolveAnchorSources(
  intl: IntlShape,
  sources: readonly ObjectWorkspaceAnchorSource[]
): ObjectWorkspaceAnchor[] {
  return sources.map((source) => ({
    id: source.id,
    kind: source.kind,
    title: intl.formatMessage(source.title),
    occurredAt: source.occurredAt,
    detail: source.detail ? intl.formatMessage(source.detail) : undefined,
  }));
}

function PatientHistoryFacet({
  onSelect,
}: {
  onSelect?: (anchor: ObjectWorkspaceAnchor) => void;
}): JSX.Element {
  const intl = useIntl();
  const anchors = useMemo(() => resolveAnchorSources(intl, PATIENT_HISTORY), [intl]);

  return (
    <AnchorSection heading={messages.historyHeading}>
      <AnchorList anchors={anchors} onSelect={onSelect} />
    </AnchorSection>
  );
}

interface PlaceholderFacetProps {
  heading: MessageDescriptor;
  description: MessageDescriptor;
  documents?: boolean;
}

function PlaceholderFacet({
  heading,
  description,
  documents = false,
}: PlaceholderFacetProps): JSX.Element {
  const intl = useIntl();
  const Icon = documents ? FolderOpen : ListChecks;

  return (
    <section className="rounded-xl border border-border bg-surface-page p-4">
      <Icon className="mb-3 size-5 text-text-secondary" aria-hidden />
      <TypographyP2SemiBold className="text-text-primary">
        {intl.formatMessage(heading)}
      </TypographyP2SemiBold>
      <TypographyP2 className="mt-1 text-text-secondary">
        {intl.formatMessage(description)}
      </TypographyP2>
      {documents ? (
        <TypographyCaption className="mt-3 text-text-tertiary">
          {intl.formatMessage(messages.noDocuments)}
        </TypographyCaption>
      ) : null}
    </section>
  );
}

export function ObjectWorkspaceFacetContent({
  facet,
  kind,
  patientDetailsState,
  patientSessionsState,
  onAnchorSelect,
  onWorkEngage,
}: {
  facet: ObjectWorkspaceFacet;
  kind: ObjectWorkspaceObject['kind'];
  patientDetailsState?: ObjectWorkspacePatientDetailsState;
  patientSessionsState?: ObjectWorkspacePatientSessionsState;
  onAnchorSelect?: (anchor: ObjectWorkspaceAnchor) => void;
  onWorkEngage?: (work: ObjectWorkspaceWorkEngagement) => void;
}): JSX.Element {
  if (facet === 'work') {
    return <ObjectWorkspaceWorkContent kind={kind} onWorkEngage={onWorkEngage} />;
  }
  if (facet === 'details' && kind === 'patient') {
    return (
      <ObjectWorkspacePatientDetails
        state={patientDetailsState ?? { status: 'ready', details: {} }}
      />
    );
  }
  if (facet === 'sessions') {
    return (
      <PatientSessionsFacet
        state={patientSessionsState ?? { status: 'ready', sessions: [] }}
        onSelect={onAnchorSelect}
      />
    );
  }
  if (facet === 'history') {
    return <PatientHistoryFacet onSelect={onAnchorSelect} />;
  }
  if (facet === 'context') {
    return (
      <PlaceholderFacet
        heading={messages.contextHeading}
        description={messages.contextDescription}
      />
    );
  }
  if (facet === 'plan') {
    return (
      <PlaceholderFacet
        heading={messages.planHeading}
        description={messages.planDescription}
      />
    );
  }
  return (
    <PlaceholderFacet
      heading={messages.documentsHeading}
      description={messages.documentsDescription}
      documents
    />
  );
}
